
import { RefObject, useEffect } from 'react';

export const FOCUSABLE_SELECTOR =
    'button:not([disabled]), ' +
    '[href]:not([disabled]), ' +
    'input:not([disabled]):not([type="hidden"]), ' +
    'select:not([disabled]), ' +
    'textarea:not([disabled]), ' +
    '[tabindex]:not([tabindex="-1"]):not([disabled])';

interface TrapFocusProps {
  modalRef: RefObject<HTMLElement | null>;
  iframeRef?: RefObject<HTMLIFrameElement | null>;
  customEvent?: string;
  onClose?: () => void;
  enabled?: boolean;
}

export const useTrapFocus = ({ modalRef, iframeRef, customEvent, onClose, enabled = true }: TrapFocusProps) => {
    const trapFocus = (e: KeyboardEvent) => {
        if (e.key !== 'Tab') return;

        const iframe = iframeRef?.current ?? null;
        const modalContainer = modalRef.current;
        if (!modalContainer) return;

        try {
            const iframeDoc = iframe?.contentWindow?.document || null;
            const modalFocusableElements = modalContainer.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
            let iframeFocusableElements: HTMLElement[] = [];
            if (iframeDoc) {
                iframeFocusableElements = Array.from(iframeDoc.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
            }
            const allFocusableElements: HTMLElement[] = [
                ...Array.from(modalFocusableElements),
                ...Array.from(iframeFocusableElements),
            ];
            if (allFocusableElements.length === 0) return;
            const firstElement = allFocusableElements[0];
            const lastElement = allFocusableElements[allFocusableElements.length - 1];
            let currentActiveElement: Element | null = document.activeElement;
            if (iframe && document.activeElement === iframe && iframeDoc) {
                currentActiveElement = iframeDoc.activeElement;
            }
            const isCurrentElementInModal =
                allFocusableElements.includes(currentActiveElement as HTMLElement) ||
                (currentActiveElement && modalContainer.contains(currentActiveElement));
            if (!isCurrentElementInModal) {
                e.preventDefault();
                firstElement.focus();
                return;
            }
            if (e.shiftKey && currentActiveElement === firstElement) {
                e.preventDefault();
                lastElement.focus();
            } else if (!e.shiftKey && currentActiveElement === lastElement) {
                e.preventDefault();
                firstElement.focus();
            }
        } catch (error) {
            e.preventDefault();
            console.warn('Focus trap fallback:', error);
        }
    };

    const globalFocusTrap = (e: KeyboardEvent) => {
        if (e.key !== 'Tab') return;
        const modalContainer = modalRef.current;
        if (!modalContainer) return;
        const iframe = iframeRef?.current ?? null;
        const isOutsideModal =
        !modalContainer.contains(document.activeElement) &&
        document.activeElement !== iframe;
        if (isOutsideModal) {
            e.preventDefault();
            const firstFocusable = modalContainer.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
            if (firstFocusable) firstFocusable.focus();
            else if (iframe) iframe.focus();
        }
    };

    // Catches focus escaping via cross-origin iframes: Tab key events inside a cross-origin iframe never propagate to the parent, so globalFocusTrap
    // cannot intercept them. focusin fires in the main document whenever focus arrives here from anywhere — including a silent cross-origin iframe exit.
    const handleFocusIn = (e: FocusEvent) => {
        const modalContainer = modalRef.current;
        const iframe = iframeRef?.current ?? null;
        if (!modalContainer) return;
        const target = e.target as Element;
        const isInsideModal = modalContainer.contains(target);
        const isIframe = target === iframe;
        if (!isInsideModal && !isIframe) {
            const firstFocusable = modalContainer.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
            if (firstFocusable) firstFocusable.focus();
            else if (iframe) iframe.focus();
        }
    };

    const handleIframeLoad = () => {
        const iframe = iframeRef?.current ?? null;
        if (!iframe || !iframe.contentWindow) return;
        try {
            const iframeDoc = iframe.contentWindow.document;
            iframeDoc.addEventListener('keydown', trapFocus);

            const firstFocusable = iframeDoc.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
            if (firstFocusable) {
                setTimeout(() => firstFocusable.focus(), 100);
            }
        } catch (err) {
            console.warn('Cannot access iframe content:', err);
        }
    };

    useEffect(() => {
        // When suspended (e.g. a Redux modal with its own focus trap is open on top),
        // attach nothing so the two traps cannot fight over focus.
        if (!enabled) return;
        const modalContainer = modalRef.current;
        document.addEventListener('keydown', globalFocusTrap, true);
        document.addEventListener('focusin', handleFocusIn);
        modalContainer?.addEventListener('keydown', trapFocus);
        if (customEvent && onClose) {
            const closeModalFromIframe = (e: CustomEvent) => {
                if (e.detail.event === customEvent) {
                    onClose();
                }
            };
            (window as any).customEvent = customEvent;
            window.addEventListener(customEvent, closeModalFromIframe);
            return () => {
                delete (window as any).customEvent;
                window.removeEventListener(customEvent, closeModalFromIframe);
                document.removeEventListener('keydown', globalFocusTrap, true);
                document.removeEventListener('focusin', handleFocusIn);
                modalContainer?.removeEventListener('keydown', trapFocus);
                try {
                    iframeRef?.current?.contentWindow?.document?.removeEventListener('keydown', trapFocus);
                } catch (e) {
                // ignore
                }
            };
        }
        return () => {
            document.removeEventListener('keydown', globalFocusTrap, true);
            document.removeEventListener('focusin', handleFocusIn);
            modalContainer?.removeEventListener('keydown', trapFocus);
            try {
                iframeRef?.current?.contentWindow?.document?.removeEventListener('keydown', trapFocus);
            } catch (e) {
                // ignore
            }
        };
    }, [customEvent, enabled]);

    return { handleIframeLoad };
};
